import { useEffect, useState } from "react";
import { apiClient, formatApiError, uploadFile, resolveUrl, API } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import { Upload, Save, ExternalLink } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost } from "@/components/admin/ui";

export default function SeoPage() {
    const { settings, refresh } = useSettings();
    const [form, setForm] = useState(settings);

    useEffect(() => { setForm(settings); }, [settings]);

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const r = await uploadFile(f);
            setForm({ ...form, og_image: r.url });
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put("/settings", form);
            await refresh();
            toast.success("SEO settings saved.");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <PageHeader
                title="SEO management"
                subtitle="Page title, description, keywords and sharing preview."
                action={<a href={`${API}/sitemap.xml`} target="_blank" rel="noreferrer" className={btnGhost}><ExternalLink size={14} /> View sitemap.xml</a>}
            />
            <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="seo-form">
                <Card className="p-6">
                    <h3 className="font-serif text-xl mb-5">Meta tags</h3>
                    <div className="space-y-4">
                        <Field label="Meta title (≤ 60 chars)"><input data-testid="seo-title" maxLength={70} className={inputCls} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></Field>
                        <Field label="Meta description (≤ 160 chars)"><textarea data-testid="seo-description" maxLength={200} rows={3} className={inputCls + " resize-none"} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></Field>
                        <Field label="Keywords (comma-separated)"><input data-testid="seo-keywords" className={inputCls} value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} /></Field>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-serif text-xl mb-5">Open Graph (social sharing)</h3>
                    <Field label="OG image (1200×630 recommended)">
                        <div className="flex gap-3 items-start">
                            {form.og_image && <img src={resolveUrl(form.og_image)} alt="" className="w-32 h-20 object-cover bg-sand" />}
                            <div className="flex-1 space-y-2">
                                <input className={inputCls} value={form.og_image} onChange={(e) => setForm({ ...form, og_image: e.target.value })} />
                                <label className={btnGhost + " cursor-pointer"}><Upload size={14} /> Upload<input type="file" accept="image/*" onChange={upload} className="hidden" /></label>
                            </div>
                        </div>
                    </Field>
                </Card>

                <button data-testid="seo-save" className={btnPrimary}><Save size={14} /> Save SEO settings</button>
            </form>
        </>
    );
}
